/**
 * V8 게임 토큰 파서. SDK auth 형식 (base64(JSON({msg, signature}))) 에서 inner msg 를
 * 디코드해 account / verse / exp 를 추출한다.
 *
 * Browser + Node 양쪽에서 동작 — `globalThis.atob` 사용 (Node 16+, Bun, 브라우저 모두 지원).
 *
 * 서명 검증은 V8 검증 자료 미공개로 v1 에서 skip — 위조 방지는 V8 cross-account state 검증에 위임.
 */

import type { ParsedV8GameToken } from './types';

const TAG = '[parseV8GameToken]';

function decodeBase64(b64: string): string {
  // atob 는 binary string 만 반환하므로 UTF-8 안전하게 읽기 위해 escape/unescape 우회.
  // 게임 토큰은 ASCII JSON 이라 사실상 문제 없지만 호환성 차원에서 Uint8Array 경유.
  const bin = globalThis.atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

export function parseV8GameToken(authBase64: string): ParsedV8GameToken {
  let outer: { msg?: string; signature?: string };
  try {
    outer = JSON.parse(decodeBase64(authBase64));
  } catch (e) {
    throw new Error(`${TAG} outer decode failed: ${e instanceof Error ? e.message : String(e)}`);
  }
  if (!outer.msg || !outer.signature) {
    throw new Error(`${TAG} missing msg/signature`);
  }
  let inner: { verse?: string; account?: string; exp?: number };
  try {
    inner = JSON.parse(decodeBase64(outer.msg));
  } catch (e) {
    throw new Error(`${TAG} inner decode failed: ${e instanceof Error ? e.message : String(e)}`);
  }
  if (!inner.account || !inner.verse || typeof inner.exp !== 'number') {
    throw new Error(`${TAG} missing required fields (account/verse/exp)`);
  }
  return { account: inner.account, verse: inner.verse, exp: inner.exp };
}
